import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { legalConfig, useConfig } from '@entities/config/api';
import { ConsentSettingsButton } from '@features/consent';
import { ROUTES } from '@shared/config/routes';
import { LOCALE_LABELS, SUPPORTED_LOCALES, type SupportedLocale } from '@shared/i18n';
import { Container, Logo } from '@shared/ui';

import styles from './Footer.module.css';

function ApiStatus() {
  const { t } = useTranslation();
  const { isPending, isError } = useConfig();

  const state = isPending ? 'checking' : isError ? 'offline' : 'online';

  return (
    <p className={styles.apiStatus} data-state={state}>
      <span className={styles.apiDot} aria-hidden="true" />
      {t('api.label')}: {t(`api.${state}`)}
    </p>
  );
}

export function Footer() {
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();
  const current = i18n.resolvedLanguage ?? 'de';
  // Заявление о доступности показывается, только если оператор объявил себя
  // обязанным по BFSG: обещание соответствия без аудита — такое же ложное
  // утверждение, как «мы проверили автомобиль».
  const { data: config } = useConfig();
  const accessibility = legalConfig(config)?.accessibility_statement_enabled ?? false;

  return (
    <footer className={styles.footer}>
      <Container>
        <div className={styles.top}>
          <div className={styles.brandColumn}>
            {/* Логотип в подвале — ссылка на главную, как и в шапке: иначе
                поведение одного и того же знака зависит от того, где он стоит. */}
            <Link to={ROUTES.home} className={styles.brandLink} aria-label={t('brand.name')}>
              <Logo inverse />
            </Link>
            <p className={styles.tagline}>{t('footer.tagline')}</p>
            {import.meta.env.DEV ? <ApiStatus /> : null}
          </div>

          <nav className={styles.linksColumn} aria-label={t('footer.product')}>
            <h2 className={styles.columnTitle}>{t('footer.product')}</h2>
            <Link to={`${ROUTES.home}#how-it-works`}>{t('nav.howItWorks')}</Link>
            <Link to={ROUTES.about}>{t('nav.about')}</Link>
            <Link to={ROUTES.faq}>{t('nav.faq')}</Link>
            <Link to={ROUTES.app}>{t('nav.mySearches')}</Link>
          </nav>

          {/* Обязательный набор ссылок: § 5 DDG (Impressum), Art. 13 DSGVO
              (Datenschutz), § 305 Abs. 2 BGB (AGB), Art. 246a §1 Abs. 2 EGBGB
              (Widerrufsbelehrung), § 356a Abs. 1 BGB (сама функция отзыва),
              § 25 TDDDG (cookie). Все доступны без входа и без оплаты. */}
          <nav className={styles.linksColumn} aria-label={t('footer.legal')}>
            <h2 className={styles.columnTitle}>{t('footer.legal')}</h2>
            <Link to={ROUTES.imprint}>{t('footer.imprint')}</Link>
            <Link to={ROUTES.privacy}>{t('footer.privacy')}</Link>
            <Link to={ROUTES.terms}>{t('footer.terms')}</Link>
            <Link to={ROUTES.withdrawal}>{t('footer.withdrawal')}</Link>
            <Link to={ROUTES.withdrawalForm}>{t('footer.withdrawalAction')}</Link>
            <Link to={ROUTES.cookies}>{t('footer.cookies')}</Link>
            {accessibility ? (
              <Link to={ROUTES.accessibility}>{t('footer.accessibility')}</Link>
            ) : null}
            {/* Отзыв согласия обязан быть так же прост, как его дача (§ 25
                TDDDG). Кнопка не рисуется, если согласовывать нечего. */}
            <ConsentSettingsButton />
          </nav>

          <div className={styles.linksColumn}>
            <h2 className={styles.columnTitle}>{t('footer.language')}</h2>
            <div className={styles.localeSwitcher} role="group" aria-label={t('footer.language')}>
              {SUPPORTED_LOCALES.map((locale: SupportedLocale) => (
                <button
                  key={locale}
                  type="button"
                  className={styles.localeButton}
                  aria-pressed={current.startsWith(locale)}
                  onClick={() => {
                    void i18n.changeLanguage(locale);
                  }}
                >
                  {LOCALE_LABELS[locale]}
                </button>
              ))}
            </div>
          </div>
        </div>

        <p className={styles.rights}>{t('footer.rights', { year })}</p>
      </Container>
    </footer>
  );
}
